import type { AccountInfo, Connection, InflationReward, ParsedAccountData } from "@solana/web3.js";
import { PublicKey, StakeProgram } from "@solana/web3.js";
import BN from "bn.js";
import type { Infer } from "superstruct";
import { coerce, create, enums, instance, nullable, number, string, type } from "superstruct";

export const BigNumFromString = coerce(instance(BN), string(), (value) => {
  if (typeof value === "string") return new BN(value, 10);
  throw new Error("invalid big num");
});

export const PublicKeyFromString = coerce(instance(PublicKey), string(), (value) => new PublicKey(value));

export type StakeAccountType = Infer<typeof StakeAccountType>;
export const StakeAccountType = enums(["uninitialized", "initialized", "delegated", "rewardsPool"]);

export type StakeMeta = Infer<typeof StakeMeta>;
export const StakeMeta = type({
  rentExemptReserve: BigNumFromString,
  authorized: type({
    staker: PublicKeyFromString,
    withdrawer: PublicKeyFromString,
  }),
  lockup: type({
    unixTimestamp: number(),
    epoch: number(),
    custodian: PublicKeyFromString,
  }),
});

export type StakeAccountInfo = Infer<typeof StakeAccountInfo>;
export const StakeAccountInfo = type({
  meta: StakeMeta,
  stake: nullable(
    type({
      delegation: type({
        voter: PublicKeyFromString,
        stake: BigNumFromString,
        activationEpoch: BigNumFromString,
        deactivationEpoch: BigNumFromString,
        warmupCooldownRate: number(),
      }),
      creditsObserved: number(),
    }),
  ),
});

export type StakeAccount = Infer<typeof StakeAccount>;
export const StakeAccount = type({
  type: StakeAccountType,
  info: StakeAccountInfo,
});

export interface StakeAccountMeta {
  address: PublicKey;
  seed: string;
  lamports: number;
  stakeAccount: StakeAccount;
  inflationRewards: InflationReward[];
}

export function accountInfoToStakeAccount(account: AccountInfo<Buffer | ParsedAccountData>): StakeAccount | undefined {
  return ("parsed" in account?.data && create(account.data.parsed, StakeAccount)) || undefined;
}

export function sortStakeAccountMetas(stakeAccountMetas: StakeAccountMeta[]) {
  stakeAccountMetas.sort((a, b) => {
    if (a.seed < b.seed) {
      return -1;
    } else if (a.seed > b.seed) {
      return 1;
    }
    return 0;
  });
}

export async function findStakeAccountMetas(
  connection: Connection,
  walletAddress: PublicKey,
): Promise<StakeAccountMeta[]> {
  const newStakeAccountMetas: StakeAccountMeta[] = [];

  // Create potential solflare seed PDAs
  const solflareStakeAccountSeedPubkeys = await Promise.all(
    Array.from(Array(20).keys()).map(async (i) => {
      const seed = `stake:${i}`;
      return PublicKey.createWithSeed(walletAddress, seed, StakeProgram.programId).then((pubkey) => ({ seed, pubkey }));
    }),
  );

  const naturalStakeAccountSeedPubkeys = await Promise.all(
    Array.from(Array(20).keys()).map(async (i) => {
      const seed = `${i}`;
      return PublicKey.createWithSeed(walletAddress, seed, StakeProgram.programId).then((pubkey) => ({ seed, pubkey }));
    }),
  );

  const parsedStakeAccountsStakeAuthority = await connection.getParsedProgramAccounts(StakeProgram.programId, {
    filters: [
      {
        memcmp: {
          offset: 12,
          bytes: walletAddress.toBase58(),
        },
      },
    ],
  });

  const parsedStakeAccountsWithdrawalAuthority = await connection.getParsedProgramAccounts(StakeProgram.programId, {
    filters: [
      {
        memcmp: {
          offset: 44,
          bytes: walletAddress.toBase58(),
        },
      },
    ],
  });

  const parsedStakeAccounts = parsedStakeAccountsStakeAuthority
    .concat(parsedStakeAccountsWithdrawalAuthority)
    .filter((stake, index, self) => index === self.findIndex((t) => t.pubkey.equals(stake.pubkey)));

  parsedStakeAccounts.forEach(({ pubkey, account }) => {
    const stakeAccount = accountInfoToStakeAccount(account);
    if (!stakeAccount) {
      return;
    }

    // We identify accounts with the solflare seed, or natural seed only for now
    const matchingSolflareSeed = solflareStakeAccountSeedPubkeys.find((element) => element.pubkey.equals(pubkey))?.seed;
    const matchingNaturalSeed = naturalStakeAccountSeedPubkeys.find((element) => element.pubkey.equals(pubkey))?.seed;
    const seed = matchingSolflareSeed || matchingNaturalSeed || `${pubkey.toBase58().slice(12)}...`;

    const balanceLamports = account.lamports;
    newStakeAccountMetas.push({
      address: pubkey,
      seed,
      lamports: balanceLamports,
      stakeAccount,
      inflationRewards: [],
    });
  });

  /*const epochInfo = await connection.getEpochInfo();

  const delegatedActivationEpochs = newStakeAccountMetas
    .filter((meta) => meta.stakeAccount.info.stake?.delegation.activationEpoch)
    .map(
      (meta) =>
        meta.stakeAccount.info.stake?.delegation.activationEpoch?.toNumber() ??
        1000
    ); // null coallescing not possible here

  if (delegatedActivationEpochs.length !== 0) {
    const minEpoch = Math.min(...delegatedActivationEpochs);

    let startEpoch = epochInfo.epoch - 1; // No rewards yet for the current epoch, so query from previous epoch
    const tasks: (() => Promise<(InflationReward | null)[]>)[] = [];
    for (let epoch = startEpoch; epoch > minEpoch; epoch--) {
      tasks.push(() =>
        connection.getInflationReward(
          newStakeAccountMetas.map((accountMeta) => accountMeta.address),
          epoch,
          "finalized"
        )
      );
    }

    sortStakeAccountMetas(newStakeAccountMetas);

    let inflationRewardsResults: (InflationReward | null)[][] = [];
    await Promise.all(
      chunks(tasks, 4).map(
        async (chunk) =>
          (inflationRewardsResults = inflationRewardsResults.concat(
            await Promise.all(chunk.map((task) => task()))
          ))
      )
    );

    inflationRewardsResults.forEach((inflationRewards) =>
      inflationRewards.forEach((inflationReward, index) => {
        if (inflationReward) {
          newStakeAccountMetas[index].inflationRewards.push(inflationReward);
        }
      })
    );
  }*/

  sortStakeAccountMetas(newStakeAccountMetas);

  return newStakeAccountMetas;
}
